import { useEffect, useState } from 'react'
import { useQueries } from 'react-query'

import { useIdentity, useLogger } from '@vp/ubik-context'

import type { Project } from '../../../../types/Project'
import type { WorkId } from '../../../../types/WorkId'
import { loadProject } from '../ProjectsContext/ProjectsContextHelpers'
import { useAuthorization } from '../AuthorizationProvider'
import { useTenant } from '../TenantContext'

export interface IUseProjectLoaderResult {
  isFetching: boolean
  isError: boolean
  isSuccess: boolean
  projects: Project[]
}

export const useProjectLoader = (culture: string, ...workIds: WorkId[]): IUseProjectLoaderResult => {
  const { isIdentityInitialized } = useIdentity()
  const { authorizationHeader } = useAuthorization()
  const { tenant, cartTenant } = useTenant()
  const logger = useLogger()
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [projects, setProjects] = useState<Project[]>([])

  const projectsResults = useQueries(
    workIds.map((workId: WorkId) => {
      return {
        queryKey: ['work-entity', tenant, workId],
        queryFn: async () => await loadProject(tenant, cartTenant, culture, workId, authorizationHeader!, logger), // authorizationHeader is defined in enabled condition
        enabled: !!authorizationHeader && isIdentityInitialized,
        staleTime: Infinity,
      }
    }),
  )

  useEffect(() => {
    const fetching = projectsResults.some((x) => x.isFetching)
    const error = projectsResults.some((x) => x.isError)
    const success = projectsResults.every((x) => x.isSuccess)

    setIsError(error)
    setIsSuccess(success)
    setIsFetching(fetching)
  }, [projectsResults])

  useEffect(() => {
    if (isSuccess) {
      setProjects(projectsResults.filter((x) => !!x.data).map((proj) => proj.data as Project))
    }
  }, [isSuccess, projectsResults.length])

  return {
    isSuccess,
    isFetching,
    isError,
    projects,
  }
}
